import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import ServiceButtons from '../../components/feature/ServiceButtons';
import { useEffect } from 'react';

export default function About() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'About Us - Virtus Realty Appraisal Services LLC';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Learn about Virtus Realty Appraisal Services LLC and our founder. Certified General appraisers providing professional property valuation services throughout New Jersey.');
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'about Virtus Realty, certified appraiser, New Jersey appraisal company, professional appraisers, property valuation experts');
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Virtus Realty Appraisal Services LLC",
      "description": "Learn about our certified appraisal services and experienced team",
      "mainEntity": {
        "@type": "Organization",
        "name": "Virtus Realty Appraisal Services LLC",
        "description": "Certified General appraisers providing professional property valuation services throughout New Jersey",
        "telephone": "(551) 206-2485",
        "email": "virtusappraisalsnj@gmail.com",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "NJ",
          "addressCountry": "US"
        },
        "areaServed": {
          "@type": "State",
          "name": "New Jersey"
        }
      }
    });
    document.head.appendChild(script);
    
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const expertise = [
    'Commercial & Industrial Appraisals',
    'Residential & Multi-Family Appraisals',
    'Estate & Trust Valuations',
    'Tax Appeal & Litigation Support',
    'Bank & Mortgage Financing Appraisals'
  ];

  const whyChooseUs = [
    {
      icon: 'ri-shield-check-line',
      title: 'Certified, Licensed & Insured',
      description: 'Our team holds the highest level of professional certifications and maintains comprehensive insurance coverage.'
    },
    {
      icon: 'ri-bank-line',
      title: 'Trusted by Institutions',
      description: 'Banks, attorneys, and government entities rely on our accurate and defensible valuation reports.'
    },
    {
      icon: 'ri-file-text-line',
      title: 'Detailed, Defensible Reports',
      description: 'Every report meets USPAP standards with comprehensive analysis and clear documentation.'
    },
    {
      icon: 'ri-time-line',
      title: 'Fast Turnaround Times',
      description: 'We understand time-sensitive transactions and deliver reports promptly without compromising quality.'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Local Market Insight',
      description: 'Deep local knowledge combined with national standards ensures accurate market-based valuations.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-24 flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/0fa5f7c387d670893dc7052b4aff624e.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight font-elegant">
              Precision. Integrity. <span className="text-gradient-gold">Valuation Excellence.</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 leading-relaxed font-modern max-w-3xl mx-auto">
              Virtus Realty Appraisal Services LLC delivers independent, certified real estate appraisal and consulting services with unwavering commitment to accuracy and professionalism.
            </p>
            <div className="mt-8">
              <p className="text-lg text-gold-300 font-semibold font-elegant">
                "Certified. Independent. Trusted."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Overview Section */}
      <section id="company" className="py-20 bg-white scroll-mt-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-6 font-elegant">
                About Virtus Realty Appraisal Services LLC
              </h2>
              <div className="space-y-6 text-gray-700 font-modern">
                <p className="text-lg leading-relaxed">
                  Virtus Realty Appraisal Services LLC provides independent, certified real estate appraisal and consulting services for clients across the commercial, residential, and industrial markets.
                </p>
                <p className="text-lg leading-relaxed">
                  Founded on the principles of virtue, accuracy, and professionalism, our firm delivers objective and defensible valuation reports that support sound financial and legal decisions.
                </p>
                <p className="text-lg leading-relaxed">
                  Whether for lending, estate planning, taxation, or investment analysis, we offer every client the same level of precision, transparency, and respect that defines our name — <strong className="text-gold-600">Virtus</strong>.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20certified%20appraiser%20in%20business%20attire%20reviewing%20property%20documents%20and%20blueprints%20in%20modern%20office%2C%20sophisticated%20workspace%20with%20gold%20accents%2C%20natural%20lighting%2C%20detailed%20analysis%20and%20measurement%20tools%20on%20desk&width=600&height=500&seq=company-overview&orientation=portrait"
                alt="Professional appraiser reviewing property documents"
                className="rounded-xl shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-8 font-elegant">
              Our Mission
            </h2>
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-200">
              <p className="text-xl text-gray-700 leading-relaxed font-modern mb-6">
                Our mission is to deliver accurate, unbiased, and timely real estate appraisals that empower our clients to make informed decisions.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed font-modern">
                We believe that trust is earned through diligence, experience, and integrity — values reflected in every report we prepare.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About the Founder Section */}
      <section id="founder" className="py-20 bg-gray-50 scroll-mt-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-4 font-elegant">
              About the Founder
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-modern">
              Meet the experienced professional leading Virtus Realty Appraisal Services LLC
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/0c5a07476bd7a7109b05d9670ab2ad74/085552a04fb0f382dae98df2f0d096c9.jpeg"
                alt="Peter Gerard Mouskourie, SCGREA, CTA - Founder and Lead Appraiser"
                className="rounded-xl shadow-lg object-cover w-full h-96 object-top"
              />
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-black-900 mb-4 font-elegant">
                Peter Gerard Mouskourie, SCGREA, CTA<br />
              </h3>
              <p className="text-lg text-black-900 mb-6 font-elegant">
                SCGREA #42RG00303500 - CTA #2653
              </p>
              <p className="text-lg text-gold-600 mb-6 font-semibold font-elegant">
                Founder & Lead Appraiser
              </p>
              
              <div className="space-y-6 text-gray-700 font-modern">
                <p className="text-lg leading-relaxed">
                  Peter Gerard Mouskourie, SCGREA, CTA is the founder and lead appraiser of Virtus Realty Appraisal Services LLC. He is a State Certified General Real Estate Appraiser, as well as a State Certified Tax Assessor with years of experience in the valuation of commercial, residential, and industrial properties, & special purpose properties. He brings a disciplined and analytical approach to every assignment. Emphasis has been on ad valorem appraisals as well as fair market valuations.
                </p>
                <p className="text-lg leading-relaxed">
                  As a State Certified General Appraiser, Peter Gerard has performed appraisals for banks, attorneys, estate representatives, and private clients, ensuring every report is credible, compliant, and defensible.
                </p>
                <p className="text-lg leading-relaxed">
                  Guided by the firm's core values of accuracy, integrity, and professionalism, Peter Gerard founded Virtus Realty Appraisal Services LLC to provide clients with valuation services built on trust and expertise.
                </p>
                
                <div className="bg-white rounded-lg p-6 border-l-4 border-gold-500 shadow-sm">
                  <p className="text-lg italic text-gray-600 font-elegant">
                    "Each property tells a story — our role is to interpret its true value with clarity and confidence."
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Education Section */}
          <div className="mt-16 bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-black-900 mb-6 font-elegant">
              Appraisal Related Education
            </h3>
            <p className="text-lg text-gold-600 mb-6 font-semibold">
              Total 300+ Classroom Hours & over 3,000 Hours of Experience
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center text-gray-700 font-modern">
                  <div className="w-2 h-2 bg-gold-500 rounded-full mr-3"></div>
                  Appraisal Principals
                </div>
                <div className="flex items-center text-gray-700 font-modern">
                  <div className="w-2 h-2 bg-gold-500 rounded-full mr-3"></div>
                  Appraisal Procedures
                </div>
                <div className="flex items-center text-gray-700 font-modern">
                  <div className="w-2 h-2 bg-gold-500 rounded-full mr-3"></div>
                  National Uniform Standards of Professional Appraisal Practice (USPAP)
                </div>
                <div className="flex items-center text-gray-700 font-modern">
                  <div className="w-2 h-2 bg-gold-500 rounded-full mr-3"></div>
                  General Appraiser Market Analysis Highest and Best Use
                </div>
                <div className="flex items-center text-gray-700 font-modern">
                  <div className="w-2 h-2 bg-gold-500 rounded-full mr-3"></div>
                  Statistics, Modeling and Finance
                </div>
                <div className="flex items-center text-gray-700 font-modern">
                  <div className="w-2 h-2 bg-gold-500 rounded-full mr-3"></div>
                  General Appraiser Site Valuation and Cost Approach
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center text-gray-700 font-modern">
                  <div className="w-2 h-2 bg-gold-500 rounded-full mr-3"></div>
                  General Appraiser Sales Comparison Approach
                </div>
                <div className="flex items-center text-gray-700 font-modern">
                  <div className="w-2 h-2 bg-gold-500 rounded-full mr-3"></div>
                  General Report Writing &amp; Case Studies
                </div>
                <div className="flex items-center text-gray-700 font-modern">
                  <div className="w-2 h-2 bg-gold-500 rounded-full mr-3"></div>
                  General Appraiser Income Approach
                </div>
                <div className="flex items-center text-gray-700 font-modern">
                  <div className="w-2 h-2 bg-gold-500 rounded-full mr-3"></div>
                  Commercial Appraisal Review
                </div>
                <div className="flex items-center text-gray-700 font-modern">
                  <div className="w-2 h-2 bg-gold-500 rounded-full mr-3"></div>
                  Expert Witness for Commercial Appraisers
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-6 font-elegant">
                Our Expertise
              </h2>
              <div className="space-y-6 text-gray-700 font-modern">
                <p className="text-lg leading-relaxed">
                  Led by Peter Gerard, a Certified General Appraiser, Virtus Realty Appraisal Services LLC brings extensive experience across multiple property types — from single-family homes and multi-family units to complex commercial and industrial assets.
                </p>
                <p className="text-lg leading-relaxed">
                  Our team maintains full compliance with USPAP standards and continuously adapts to evolving market data, regulatory requirements, and industry best practices.
                </p>
                <div className="mt-8">
                  <h3 className="text-xl font-semibold text-black-900 mb-4 font-elegant">Areas of Expertise:</h3>
                  <ul className="space-y-3">
                    {expertise.map((area, index) => (
                      <li key={index} className="flex items-center text-gray-700 font-modern">
                        <div className="w-2 h-2 bg-gold-500 rounded-full mr-3"></div>
                        {area}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-black-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-elegant">
              Why Choose Virtus
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-modern">
              At Virtus Realty Appraisal Services LLC, every valuation is guided by accuracy, integrity, and respect for the property and client alike.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-gray-900 rounded-xl p-6 border border-gray-800 hover:border-gold-500 transition-all duration-300">
                <div className="w-12 h-12 gradient-gold rounded-lg flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-black-900 text-xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 font-elegant">
                  {item.title}
                </h3>
                <p className="text-gray-300 font-modern">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-black-900 mb-6 font-elegant">
              Learn How Virtus Realty Appraisal Services LLC Can Support Your Appraisal Needs
            </h2>
            <p className="text-xl text-gray-600 mb-8 font-modern">
              Experience the confidence that comes with accurate and professional property valuations. Contact us today to discuss your appraisal requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="gradient-gold text-black-900 hover:shadow-gold font-semibold">
                  <i className="ri-file-text-line mr-2"></i>
                  Request an Appraisal
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-gold-500 text-gold-600 hover:bg-gold-500 hover:text-black-900 font-semibold">
                  <i className="ri-phone-line mr-2"></i>
                  Contact Us Today
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ServiceButtons />
    </div>
  );
}
